import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { processCODPayment } from "../store/reducers/paymentReducer";
import toast from "react-hot-toast";
import Header from "./../components/Header";
import Footer from "./../components/Footer";
import Stripe from "../components/Stripe";
import api from "./../api/api";
import { getEnabledPaymentMethods } from "../config/paymentMethods";
import CheckoutStepper from "../components/CheckoutStepper";

const isDev = process.env.NODE_ENV === "development";
const devLog = (...args) => {
  if (isDev) console.log(...args);
};

const Payment = () => {
  const originUrl = window.location.origin;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector(state => state.payment);
  const { currentCompany } = useSelector(state => state.init);

  const location = useLocation();
  const { price = 0, items = 0, orderId = null, customerEmail = "" } = location.state || {};
  const { userInfo } = useSelector(state => state.auth);
  const effectiveEmail = customerEmail || userInfo?.email || "";

  const [paymentMethod, setPaymentMethod] = useState("culqi");
  const [culqiInitialized, setCulqiInitialized] = useState(false);
  const culqiScriptLoaded = useRef(false);
  const [processingCulqi, setProcessingCulqi] = useState(false);

  // ✅ 1. CARGAR SCRIPT CULQI V4 - CORREGIDO
    // Procesar token (useCallback para estabilidad de dependencias)
  const processCulqiPayment = React.useCallback(
    async token => {
      devLog("Iniciando processCulqiPayment Token->", token);

      setProcessingCulqi(true);

      try {
        const tokenId = token?.id || token?.token || token;
        if (!tokenId) {
          console.error("processCulqiPayment: token inválido recibido:", token);
          toast.error("Token inválido recibido del proveedor de pago");
          setProcessingCulqi(false);
          return;
        }

        const payload = {
          token: tokenId,
          orderId: orderId,
          amount: price,
          email: token.email || token.customer_email || effectiveEmail,
        };

        devLog("Enviando request a /order/culqi-charge", payload);

        const response = await api.post("/order/culqi-charge", payload, { withCredentials: true });
        devLog("Respuesta de /order/culqi-charge:", response?.status, response?.data);
        devLog("Token->", token);
        const data = response.data;

        if (data.success) {
          toast.success("✅ Pago exitoso");
          // Guardar orderId para que la página de confirmación pueda rehidratarse si es necesario
          try {
            localStorage.setItem("orderId", data.orderId || orderId);
          } catch (e) {
            console.warn("No se pudo guardar orderId en localStorage:", e);
          }

          // Intentar cerrar el modal de Culqi si está abierto
          try {
            if (window.Culqi && typeof window.Culqi.close === "function") {
              window.Culqi.close();
              devLog("Culqi modal cerrado tras pago exitoso");
            }
          } catch (e) {
            console.warn("No se pudo cerrar el modal de Culqi:", e);
          }

          // Navegar a la ruta utilizada por el router: /order/confirm
          // Asegurarse de desactivar el estado de procesamiento antes de navegar
          setProcessingCulqi(false);

          navigate("/order/confirm", {
            state: {
              paymentMethod: "culqi",
              orderId: data.orderId || orderId,
              transactionId: data.transactionId || data.charge?.id,
              amount: price,
              chargeData: data.charge || null,
            },
          });
        } else {
          toast.error(data.message || "❌ Error al procesar el pago");
        }
      } catch (error) {
        console.error("Error en pago Culqi:", error);
        console.error("Error.response:", error.response);

        // Cerrar modal de Culqi si está abierto para asegurar que el usuario vea el mensaje de error
        if (window.Culqi && typeof window.Culqi.close === "function") {
          window.Culqi.close();
        }

        if (error.response) {
          const errorData = error.response.data;
          const msg = errorData?.message || errorData?.error || (typeof errorData === 'string' ? errorData : "Error al procesar el pago");
          toast.error(msg);
        } else if (error.request) {
          toast.error("No se pudo conectar con el servidor");
        } else {
          toast.error("Error inesperado. Intenta nuevamente.");
        }
      } finally {
        setProcessingCulqi(false);
      }
    },
    [orderId, price, effectiveEmail, navigate]
  );
  // Debug: register earliest possible listeners to catch events emitted before script inits
   useEffect(() => {
    const genericHandler = ev => {
      // Log event type and only safe summary of detail
      if (ev && ev.type && ev.type.startsWith && ev.type.startsWith("culqi")) {
        const d = ev.detail || {};
        const summary = { id: d?.id || d?.token?.id, email: d?.email || d?.token?.email };
        devLog("Early Culqi event:", ev.type, summary);
      }
    };

    const messageHandler = ev => {
      // Process Culqi postMessage tokens safely (avoid logging full card data)
      try {
        if (!ev || !ev.data) return;
        const origin = ev.origin || "";
        const data = ev.data;
        // Detect Culqi origin or payload format
        const isCulqiOrigin = origin.includes("culqi.com");
        const isTokenPayload = data && (data.object === "token" || data.type === "token" || data.id);

        if (isCulqiOrigin && isTokenPayload) {
          const token = data;
          devLog("Culqi postMessage token id:", token.id, "email:", token.email);
          // Forward token to processing
          processCulqiPayment(token);
      
        } else if (isCulqiOrigin) {
          devLog("window message from Culqi:", origin, "summary:", { type: data?.type || data?.object });
        }
      } catch (e) {
        console.warn("Error handling postMessage:", e);
      }
    };

    window.addEventListener("culqi", genericHandler);
    window.addEventListener("culqi:token", genericHandler);
    window.addEventListener("culqi:error", genericHandler);
    window.addEventListener("culqi:open", genericHandler);
    window.addEventListener("culqi:close", genericHandler);
    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("culqi", genericHandler);
      window.removeEventListener("culqi:token", genericHandler);
      window.removeEventListener("culqi:error", genericHandler);
      window.removeEventListener("culqi:open", genericHandler);
      window.removeEventListener("culqi:close", genericHandler);
      window.removeEventListener("message", messageHandler);
    };
  }, [processCulqiPayment]);





  // NUEVA función para confirmar cargos ya creados (Yape, Billeteras)
  const confirmCulqiCharge = React.useCallback(
    async charge => {
      devLog("Iniciando confirmCulqiCharge con Charge ID->", charge.id);
      setProcessingCulqi(true);

      try {
        const payload = {
          charge_id: charge.id,
          orderId: orderId,
          amount: price,
          email: charge.email || effectiveEmail,
        };

        devLog("Enviando request a /order/culqi-charge con charge_id", payload);

        const response = await api.post("/order/culqi-charge", payload, { withCredentials: true });
        const data = response.data;

        if (data.success) {
          toast.success("✅ Pago con Yape exitoso");
          localStorage.setItem("orderId", data.orderId || orderId);

          if (window.Culqi && typeof window.Culqi.close === "function") {
            window.Culqi.close();
          }

          setProcessingCulqi(false);
          navigate("/order/confirm", {
            state: {
              paymentMethod: "culqi",
              orderId: data.orderId || orderId,
              transactionId: data.transactionId || data.charge?.id,
              amount: price,
              chargeData: data.charge || null,
            },
          });
        } else {
          toast.error(data.message || "❌ Error al confirmar el pago");
        }
      } catch (error) {
        console.error("Error en confirmación de pago Culqi:", error);
        const errorData = error.response?.data;
        toast.error(errorData?.message || errorData?.error || "Error al confirmar el pago");
      } finally {
        setProcessingCulqi(false);
      }
    },
    [orderId, price, effectiveEmail, navigate]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Dev helper: capture cross-origin 'Script error.' events with more context
    const devErrorHandler = event => {
      // Ignore React internal errors and only show script errors
      if (event && event.message && event.message === "Script error.") {
        console.error("Detected cross-origin script error. Event:", event);
        // Try to extract script src if available
        if (event.filename) console.error("Script filename:", event.filename);
      }
    };
    if (process.env.NODE_ENV === "development") window.addEventListener("error", devErrorHandler);
    const loadCulqiScript = () => {
      if (!culqiScriptLoaded.current && paymentMethod === "culqi") {
        const script = document.createElement("script");
        script.src = "https://checkout.culqi.com/js/v4";
        script.async = true;
        script.defer = true;
        // permitir detalles de error cross-origin si el servidor lo soporta
        script.crossOrigin = "anonymous";
        // Shim global lowercase `culqi` to avoid ReferenceError thrown
        // by parts of the v4 bundle that call `culqi()` directly.
        // The shim will queue calls until `window.Culqi` is available
        // and then forward them to `window.Culqi.open`.
        if (!window.culqi) {
          window.culqi = function (...args) {
            if (window.Culqi && typeof window.Culqi.open === "function") {
              try {
                return window.Culqi.open(...args);
              } catch (e) {
                console.warn("culqi shim forward error", e);
              }
            }
            (window.culqi._q = window.culqi._q || []).push(args);
          };
        }
        script.onload = () => {
          devLog("✅ Script Culqi Checkout v2 cargado");
          culqiScriptLoaded.current = true;

          // ✅ CONFIGURAR PUBLIC KEY INMEDIATAMENTE - ESTO ES CRÍTICO
          if (window.Culqi) {
            window.Culqi.publicKey = process.env.REACT_APP_CULQI_PUBLIC_KEY;
            devLog("✅ Public Key configurada:", window.Culqi.publicKey?.substring(0, 12) + "...");

            // Intentar inicializar inline para evitar dependencias de hook
            setTimeout(() => {
              try {
                const validOrderString = getValidOrderString(orderId);
                const settings = {
                  title: "Culqi TiendaVirtual inicio",
                  currency: "PEN",
                  amount: Math.round(price * 100),
                  order: validOrderString,
                  email: effectiveEmail || "cliente@ejemplo.com",
                };

                const paymentMethods = {
                  tarjeta: true,
                  yape: true,
                  billetera: false,
                  bancaMovil: false,
                  agente: false,
                  cuotealo: false,
                };

                const options = {
                  lang: "es",
                  installments: true,
                  modal: true,
                  paymentMethods: paymentMethods,
                  appearance: {
                    theme: "default",
                    menuType: "sidebar",
                    logo: `${originUrl}/logo.png`,
                    buttonCardPayText: `Pagar S/${price.toFixed(2)}`,
                    defaultStyle: {
                      bannerColor: "#ae1d22",
                      buttonBackground: "#ae1d22",
                      menuColor: "#ae1d22",
                      buttonTextColor: "#ffffff",
                    },
                    hiddenEmail: !effectiveEmail,
                  },
                };

                window.Culqi.settings(settings);
                window.Culqi.options(options);
                setCulqiInitialized(true);

                // Flush queued lowercase shim calls
                if (window.culqi && Array.isArray(window.culqi._q) && window.culqi._q.length) {
                  window.culqi._q.forEach(args => {
                    try {
                      window.Culqi.open(...args);
                    } catch (e) {
                      console.warn("culqi shim replay error", e);
                    }
                  });
                  delete window.culqi._q;
                }

                // Registrar listeners de Culqi (global + namespaced)
                const _handleCulqiEvent = (ev) => {
                  const detail = ev.detail;
                  devLog("Evento Culqi recibido:", { type: ev.type, detail });

                  if (!detail) return;

                  // Flujo para Yape/Billeteras (devuelve un objeto 'charge')
                  if (detail.object === 'charge') {
                    if (detail.outcome?.type === 'venta_exitosa') {
                      devLog("Charge object exitoso recibido, confirmando pago...", detail);
                      confirmCulqiCharge(detail);
                    } else {
                      devLog("Charge object con error recibido", detail);
                      toast.error(detail.outcome?.user_message || "El pago no se completó.");
                      setProcessingCulqi(false);
                    }
                    return;
                  }

                  // Flujo para Tarjetas (devuelve un objeto 'token')
                  if (detail.object === 'token') {
                     devLog("Token object recibido, procesando pago...", detail);
                     processCulqiPayment(detail);
                     return;
                  }

                  // Manejo de errores explícitos
                  if (detail.object === 'error') {
                     devLog("Error object explícito recibido", detail);
                     toast.error(detail.user_message || "Error en el pago");
                     setProcessingCulqi(false);
                     return;
                  }
                };

                window.addEventListener("culqi", _handleCulqiEvent);
                const _closeHandler = () => { devLog("Culqi modal cerrado."); };
                const _openHandler = () => { devLog("Culqi modal abierto."); };
                window.addEventListener("culqi:close", _closeHandler);
                window.addEventListener("culqi:open", _openHandler);

                window._culqiEventHandlers = {
                  global: _handleCulqiEvent,
                  closeHandler: _closeHandler,
                  openHandler: _openHandler,
                };
              } catch (e) {
                console.error("Error inicializando Culqi inline:", e);
              }
            }, 300);
          } else {
            // Si no está disponible, reintentar en breve
            setTimeout(() => {
              if (window.Culqi) {
                try {
                  window.Culqi.publicKey = process.env.REACT_APP_CULQI_PUBLIC_KEY;                  
                } catch (e) {
                  console.error("Reintento de inicialización Culqi falló:", e);
                }
              }
            }, 1000);
          }
        };
        script.onerror = err => {
          console.error("❌ Error cargando Culqi Checkout v4:", err, "event:", err?.message || err);
          toast.error("No se pudo cargar la pasarela de pago. Intenta recargar.");
        };
        document.head.appendChild(script);
      }
    };

    if (paymentMethod === "culqi" && !culqiScriptLoaded.current) {
      loadCulqiScript();
    }

    // Limpiar script cuando cambia el método de pago
    return () => {
      if (process.env.NODE_ENV === "development") window.removeEventListener("error", devErrorHandler);
      if (paymentMethod !== "culqi" && culqiScriptLoaded.current) {
        culqiScriptLoaded.current = false;
        setCulqiInitialized(false);
      }
    };
  }, [paymentMethod, orderId, price, effectiveEmail, originUrl, processCulqiPayment, confirmCulqiCharge]);

  const getValidOrderString = orderId => {
    // Convertir a string
    const orderStr = typeof orderId === "object" && orderId.toString ? orderId.toString() : String(orderId);
    let cleaned =
      "ord_live_" +
      orderStr
        .replace(/ObjectId\(/gi, "")
        .replace(/\)/gi, "")
        .replace(/['"]/g, "")
        .trim();
    return cleaned;
  };

  // ✅ 4. PROCESAR PAGO CULQI (definido arriba como useCallback para estabilidad)

  // ✅ 5. ABRIR CHECKOUT V4 - CORREGIDO
  const openCulqiCheckout = () => {
    // ✅ VERIFICACIÓN CRÍTICA DE PUBLIC KEY
    if (!window.Culqi || !culqiInitialized) {
      toast.error("Culqi no está listo. Por favor, espera unos segundos.");
      devLog("Estado:", {
        CulqiDisponible: !!window.Culqi,
        inicializado: culqiInitialized,
        publicKey: window.Culqi?.publicKey || "No configurada",
        email: effectiveEmail || "<sin email>",
      });
      return;
    }

    // ✅ VERIFICAR PUBLIC KEY ESPECÍFICAMENTE
    if (!window.Culqi.publicKey) {
      toast.error("Error de configuración: Falta clave pública");
      console.error("❌ Public Key no configurada en window.Culqi");
      return;
    }

    try {
      // Validar datos críticos antes de abrir
      if (!orderId) {
        toast.error("No se encontró el ID del pedido");
        return;
      }

      // Actualizar settings con el monto actual
      const validOrderString = getValidOrderString(orderId);
      window.Culqi.settings({
        title: "Culqi  tiendaVirtual",
        currency: "PEN", // Este parámetro es requerido para realizar pagos yape
        amount: Math.round(price * 100),
        order: validOrderString,
        email: effectiveEmail || "cliente@ejemplo.com",
      });

      devLog("🔧 Abriendo checkout con:", validOrderString);

      window.Culqi.open();
      setProcessingCulqi(true);
    } catch (error) {
      console.error("Error abriendo checkout Culqi v4:", error);
      toast.error("No se pudo abrir la ventana de pago: " + (error.message || ""));
      setProcessingCulqi(false);
    }
  };

  // Reset processingCulqi cuando el usuario cierra el modal con X
  useEffect(() => {
    const handleClose = () => {
      setProcessingCulqi(false);
    };
    window.addEventListener('culqi:close', handleClose);
    return () => window.removeEventListener('culqi:close', handleClose);
  }, []);

  // Limpiar event listeners al desmontar
  useEffect(() => {
    return () => {
      // Limpiar listeners registrados para Culqi
      if (window._culqiEventHandlers) {
        const h = window._culqiEventHandlers;
        if (h.global) window.removeEventListener("culqi", h.global);
        if (h.closeHandler) window.removeEventListener("culqi:close", h.closeHandler);
        if (h.openHandler) window.removeEventListener("culqi:open", h.openHandler);
        delete window._culqiEventHandlers;
      }
    };
  }, []);

  // Validar datos esenciales
  useEffect(() => {
    if (!orderId || price <= 0) {
      toast.error("Información de pedido incompleta");
      navigate("/carrito");
    }
  }, [orderId, price, navigate]);

  // Mostrar errores
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleCODPayment = () => {
    if (!orderId) {
      toast.error("No se encontró el ID del pedido");
      return;
    }

    dispatch(
      processCODPayment({
        orderId,
        navigate: (path, state) => navigate(path, { state }),
      })
    );
  };

  // Obtener métodos configurables (por defecto todos). Las imágenes son rutas relativas.
  const paymentMethods = getEnabledPaymentMethods();

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow bg-[#eeeeee]">
        <div className="w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto py-8">
          <h1 className="text-2xl font-bold mb-6">Método de Pago</h1>
          {currentCompany?.verCheckoutStepper && <CheckoutStepper currentStep={3} />}

          <div className="flex flex-wrap md:flex-col-reverse gap-6">
            <div className="w-7/12 md:w-full">
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="text-xl font-semibold mb-4">Selecciona método de pago</h2>

                <div className="flex flex-wrap gap-2 mb-6">
                  {paymentMethods.map(method => (
                    <div
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`w-[48%] cursor-pointer p-4 rounded-lg border transition-all ${
                        paymentMethod === method.id ? "border-[#ae1d22] bg-[#ae1d22]/10" : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                      }`}>
                      <div className="flex items-center gap-3">
                        <img src={`${originUrl}${method.image}`} alt={method.name} className="w-12 h-12 object-contain" />
                        <span className="font-medium">{method.name}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  {paymentMethod === "stripe" && <Stripe orderId={orderId} price={price} />}

                  {paymentMethod === "cod" && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Pago contra entrega</h3>
                      <p className="text-gray-600 mb-4">Pagarás en efectivo cuando recibas tu pedido.</p>
                      <button
                        onClick={handleCODPayment}
                        disabled={loading}
                        className={`px-6 py-2 rounded-md text-white bg-[#ae1d22] hover:bg-[#ae1d22]/90 transition ${loading ? "opacity-70 cursor-not-allowed" : ""}`}>
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Procesando...
                          </span>
                        ) : (
                          "CONFIRMAR PEDIDO"
                        )}
                      </button>
                    </div>
                  )}

                  {paymentMethod === "culqi" && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Pago con Tarjeta (Culqi)</h3>
                      <p className="text-gray-600 mb-4">
                        Paga con tarjeta de crédito/débito de forma segura.
                        {price > 0 && ` Total: S/${price.toFixed(2)}`}
                      </p>

                      <button
                        onClick={openCulqiCheckout}
                        disabled={loading || !culqiInitialized || processingCulqi}
                        className={`px-6 py-3 rounded-md text-white font-medium w-full transition 
                          ${loading || !culqiInitialized || processingCulqi ? "opacity-70 cursor-not-allowed bg-gray-400" : "bg-[#ae1d22] hover:bg-[#ae1d22]/90"}`}>
                        {processingCulqi ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Procesando pago...
                          </span>
                        ) : !culqiInitialized ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Inicializando Culqi...
                          </span>
                        ) : (
                          `PAGAR S/${price.toFixed(2)}`
                        )}
                      </button>

                      {/* Panel de diagnóstico mejorado (solo en development) */}
                      {isDev && (
                        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded">
                          <h4 className="text-sm font-medium mb-2">Estado de Culqi:</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Script cargado:</span>
                              <span className={`px-2 py-1 rounded text-xs ${culqiScriptLoaded.current ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                {culqiScriptLoaded.current ? "✅ Sí" : "⏳ Cargando..."}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Culqi inicializado:</span>
                              <span className={`px-2 py-1 rounded text-xs ${culqiInitialized ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                {culqiInitialized ? "✅ Listo" : "⏳ Pendiente"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Public Key:</span>
                              <span className={`px-2 py-1 rounded text-xs ${window.Culqi?.publicKey ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                {window.Culqi?.publicKey ? "✅ Configurada" : "❌ Faltante"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Order ID:</span>
                              <span className={`px-2 py-1 rounded text-xs ${getValidOrderString(orderId) ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800"}`}>
                                {getValidOrderString(orderId) ? getValidOrderString(orderId).substring(0, 10) + "..." : "❌ No"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="w-4/12 md:w-full">
              <div className="bg-white shadow rounded-lg p-5 sticky top-4">
                <h2 className="font-bold text-xl mb-4">Resumen de Pedido</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span>Productos ({items})</span>
                    <span>S/{price.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-[#ae1d22]">S/{price.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Payment;
